import React from 'react';
import Page from '../Page';
import LoadingBanner from "../../components/LoadingBanner";
import { Outlet, useLocation, useNavigate, useParams, useSearchParams, generatePath, Link } from "react-router-dom";
import TalentCommitmentLevelModal from './TalentCommitmentLevelModal';
import { useAuth } from "../../providers/AuthProvider";
import InfoTooltip from "../../components/InfoTooltip";
import TalentVersionUpdate2025 from "../talent-dashboard/TalentVersionUpdate2025";

export const talentNavLinks = (auth, constructor = TalentPortal) => {
    if (!auth) return [];
    if (auth.canManageTalent()) {
        return [
            { label: 'Opportunities', path: constructor.adminProjectsListPath() },
            { label: 'Talent Vetting', path: constructor.adminTalentListPath() },
            { label: 'Teams', path: constructor.adminTalentTeamsListPath() },
        ]
    }

    // if (!this.context?.auth?.hasCompletedProfile()) {
    //     return [
    //         {label: 'Profile', path: TalentApplication.path},
    //     ]
    // }
    if (auth.isTalentSupplierResource()) {
        return [
            { label: 'Projects', path: constructor.projectsListPath() },
            { label: 'Profile', path: '/talent/application' },
            { label: 'Teams', path: constructor.teamsListPath(), active: 'teams' },
        ]
    }

    if (auth.isClientInvitedResource({ is_on_talent_marketplace: false })) {
        return [
            { label: 'Projects', path: constructor.projectsListPath() },
            { label: 'Profile', path: '/talent/application' },
        ]
    }

    // Until the profile is approved these features aren't usable, so hide
    // them from the nav entirely (they reappear once approved).
    let baseFeaturesDisabled = false;
    if (auth.isTalent() && auth.isApplicationApproved() && !auth.isProfileApproved()) {
        baseFeaturesDisabled = true;
    }
    return [
        { label: 'Projects', path: constructor.projectsListPath(), disabled: baseFeaturesDisabled },
        { label: 'Profile', path: '/talent/application' },
        { label: 'Contracts', path: constructor.contractsPath(), disabled: baseFeaturesDisabled },
        { label: 'Jobs', path: constructor.jobBoardPath(), active: 'job-board', disabled: baseFeaturesDisabled },
        { label: 'Teams', path: constructor.teamsListPath(), active: 'teams', disabled: baseFeaturesDisabled },
        { label: 'Preferences', path: constructor.preferencesPath() },
    ].filter(link => !link.disabled)
}


class TalentPortal extends Page {

    static path = '/talent';

    static contractsPath() {
        return `/talent/contracts`
    }
    static dashboardPath() {
        return `/talent/dashboard`
    }

    static jobBoardPath() {
        return `/talent/job-board`
    }

    static preferencesPath() {
        return `/talent/work-preferences`
    }
    static adminProjectsListPath() {
        return {
            pathname: '/talent/manager/opportunities',
            key: TB.uuid()
        }
    }
    static adminTalentListPath() {
        return {
            pathname: '/talent/manager/talent-vetting',
            key: TB.uuid()
        }
    }

    static adminTalentTeamsListPath() {
        return {
            pathname: '/talent/manager/teams/all',
            key: TB.uuid()
        }
    }
    static projectsListPath(status = 'All') {
        return generatePath(`/talent/projects/:status`, { status: TB.slugify(status) })
    }
    static teamsListPath(status = 'All') {
        return generatePath(`/talent/teams/:status`, { status: TB.slugify(status) })
    }

    static supplierClientProjectsListPath(status = 'All') {
        return generatePath(`/supplier/projects/:status`, { status: TB.slugify(status) })
    }

    constructor(props, context) {
        super(props, context);

        let auth = context?.auth;

        this.state = {
            hasTodoItems: auth
                && (auth.mustSignAgreement() || !auth.hasCompletedProfile()),
            // pages: auth ? this.context?.auth?.portalPages() : [],
        }
    }

    renderTopBar() {
        let auth = this.context?.auth;
        if (!auth) {
            return <div />;
        }

        return (
            <>
                <div className="bg-black h-2.5 w-full md:hidden " />
                <div className="nav-container hidden md:flex">
                    <div className="nav-info">
                        {auth.isTalent() && (
                            <Link
                                to="dashboard"
                                className="dashboard-home-btn hidden md:flex"
                            >
                                <i className="fa fa-th text-lg text-white" />
                                <p className="text-white mb-0 mt-2">
                                    {TB.trans('talent.dashboard.nav_info')}
                                </p>
                            </Link>
                        )}

                        <div className="talent-portal-nav hiddenmd:flex">
                            {talentNavLinks(auth, this.constructor).map(({ label, path, active, disabled }) => (
                                <div key={`nav-${label}`}>
                                    <Link
                                        id={`nav-${label}`}
                                        to={path}
                                        onClick={e => disabled && e.preventDefault()} // Stops React Router navigation
                                        aria-disabled={disabled}
                                        tabIndex={disabled ? -1 : 0} // Prevents keyboard tabbing when disabled
                                        className={TB.mapClass("clickable talent-portal-nav-item p-2.5", {
                                            active: this.props.location.pathname.indexOf(active || TB.slugify(TB.toLower(label))) > 0,
                                            'feature-disabled': disabled
                                        })}
                                    >
                                        {label}
                                    </Link>
                                    {disabled && (
                                        <InfoTooltip target={`nav-${label}`} placement="bottom" type="white">
                                            <p className="text-sm text-danger mb-0 px-2 pt-2 pb-1">
                                                Not Available Yet!
                                            </p>
                                            <p className="text-xs px-2 pb-2 pt-0">
                                                Profile Needs Approval
                                            </p>
                                        </InfoTooltip>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="dashboard-logout-button hidden md:flex">
                            <button type="button" onClick={() => TB.auth().logout()} className="btn btn-outline-light">
                                Log out
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    renderTodoMessage() {
        return (
            <div className="nav-alert">
                <p>{TB.trans('talent.dashboard.nav_alert')}</p>
            </div>
        )
    }

    render() {

        if (!this.context.auth) {
            return <div className="talent-portal-page">
                <LoadingBanner />
            </div>
        }

        return (
            <div className={'talent-portal-page'}>

                {this.renderTopBar()}

                <Outlet />


            </div>
        );
    }
}

const TalentPortalLayout = (props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { auth, isLoaded } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const params = useParams();

    let queryParams = Object.fromEntries(new URLSearchParams(searchParams));

    return (
        <>
            <TalentPortal
                navigate={navigate} location={location}
                queryParams={{ ...queryParams, ...(params || {}) }}
            />

            {auth && auth.isActive() && auth.isTalent() && !auth.isTalentSupplierResource() && (
                <TalentCommitmentLevelModal />
            )}
            {auth.isTalent() && auth.isApplicationApproved() &&
                TB.isDefined(auth.talent_commitment_level) &&
                auth.has_full_time_employment_interest !== null && (
                    <TalentVersionUpdate2025 />
                )}
        </>
    )
}

export default TalentPortal;

export {
    TalentPortalLayout
}